import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { watchlistService } from '../services/watchlistService';
import { companyService } from '../services/companyService';
import { useAppData } from '../contexts/AppDataContext';
import { useLocale } from '../contexts/LocaleContext';
import { Search, Building2, Eye, Star } from 'lucide-react';
import { Company } from '../models';
import { colors } from '../config/colors';
import { spacing } from '../config/spacing';
import { Card } from '../components/UI/card';
import { Button } from '../components/UI/button';
import { Input } from '../components/UI/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/UI/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/UI/pagination';
import { TableShimmer } from '../components/UI/table-shimmer';
import {
  getRegionFromCountry,
  formatRelativeTime,
  getRiskTier,
  getCompanySizeType,
} from '../utils/companyDisplayUtils';
import toast from 'react-hot-toast';

const PAGE_SIZE = 10;
const RISK_TIERS = ['high', 'medium', 'low'];
const PRIORITIES = ['high', 'normal', 'low'];
const REGIONS = ['Asia Pacific', 'Europe', 'Americas', 'Middle East', 'Africa'];

function getIndustryBadgeStyle(industry) {
  const key = (industry || '').toLowerCase();
  if (key.includes('tech') || key.includes('software')) return { bg: colors.info[100], text: colors.info[700] };
  if (key.includes('logistic') || key.includes('transport')) return { bg: colors.success[100], text: colors.success[700] };
  if (key.includes('manufactur') || key.includes('industrial')) return { bg: colors.info[50], text: colors.info[600] };
  if (key.includes('ship') || key.includes('maritime')) return { bg: colors.success[50], text: colors.success[600] };
  return { bg: colors.secondary[100], text: colors.secondary[700] };
}

function getRiskBadgeStyle(tier) {
  if (tier === 'high') return { bg: colors.warning[100], text: colors.warning[700] };
  if (tier === 'medium') return { bg: colors.warning[50], text: colors.warning[600] };
  return { bg: colors.success[100], text: colors.success[700] };
}

const Companies = () => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { watchlist, refreshWatchlist } = useAppData();
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterRiskTier, setFilterRiskTier] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [page, setPage] = useState(1);
  const [watchlistActionId, setWatchlistActionId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const watchlistIds = useMemo(() => {
    const ids = new Set();
    (watchlist || []).forEach((item) => {
      const cid = item.companyId ?? item.company_id;
      if (cid) ids.add(cid);
    });
    return ids;
  }, [watchlist]);

  const watchlistByCompanyId = useMemo(() => {
    const byCompany = {};
    (watchlist || []).forEach((item) => {
      const cid = item.companyId ?? item.company_id;
      if (cid) byCompany[cid] = { priority: item.priority || 'normal' };
    });
    return byCompany;
  }, [watchlist]);

  const industries = useMemo(() => {
    const list = companies || [];
    return [...new Set(list.map((c) => c.industry).filter(Boolean))].sort();
  }, [companies]);

  const loadCompanies = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await companyService.getCompaniesSummary({
        page,
        size: PAGE_SIZE,
        q: debouncedSearch || undefined,
        industry: filterIndustry !== 'all' ? filterIndustry : undefined,
      });
        if (response.notModified) {
          setTableLoading(false);
          return;
        }
      if (response.success) {
        setCompanies(Array.isArray(response.data) ? response.data : []);
        setPagination(response.pagination || null);
      } else {
        setCompanies([]);
        setPagination(null);
      }
    } catch (error) {
      setCompanies([]);
      setPagination(null);
    } finally {
      setTableLoading(false);
    }
  }, [page, debouncedSearch, filterIndustry]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const cardStyle = useMemo(() => ({
    backgroundColor: colors.background.card ?? colors.background.secondary,
    borderColor: colors.border.light,
    boxShadow: spacing.shadow?.lg ?? '0 6px 18px rgba(32,41,50,0.04)',
    borderRadius: spacing.radius?.lg ?? 12,
  }), []);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filterIndustry, filterRegion, filterRiskTier, filterPriority]);

  const getCompanyRegion = (company) => {
    const country = company.country || '';
    return getRegionFromCountry(country) || country || '—';
  };

  const filteredCompanies = useMemo(() => {
    let filtered = companies || [];

    if (filterRegion !== 'all') {
      filtered = filtered.filter((c) => getCompanyRegion(c) === filterRegion);
    }
    if (filterRiskTier !== 'all') {
      filtered = filtered.filter((c) => getRiskTier(c.overallRiskScore) === filterRiskTier);
    }
    if (filterPriority !== 'all') {
      filtered = filtered.filter((c) => {
        const wl = watchlistByCompanyId[c.ticker] ?? watchlistByCompanyId[c.id];
        return wl && wl.priority === filterPriority;
      });
    }

    return filtered;
  }, [companies, filterRegion, filterRiskTier, filterPriority, watchlistByCompanyId]);

  const paginatedCompanies = filteredCompanies;

  const totalPages = pagination?.totalPages
    ?? Math.max(1, Math.ceil((pagination?.totalElements ?? filteredCompanies.length) / PAGE_SIZE));
  const totalElements = pagination?.totalElements ?? filteredCompanies.length;
  const from = totalElements === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, totalElements);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterIndustry('all');
    setFilterRegion('all');
    setFilterRiskTier('all');
    setFilterPriority('all');
    setPage(1);
  };

  const getPriorityLevel = (priority) => {
    if (priority === 'high') return 'riskLevelHigh';
    if (priority === 'low') return 'riskLevelLow';
    return 'riskLevelModerate';
  };

  const getPriorityWidth = (priority) => {
    if (priority === 'high') return 90;
    if (priority === 'low') return 25;
    return 55;
  };

  const handleToggleWatchlist = async (ticker) => {
    try {
      setWatchlistActionId(ticker);
      const isInWatchlist = watchlistIds.has(ticker);
      if (isInWatchlist) {
        const result = await watchlistService.removeFromCurrentUserWatchlist(ticker);
        if (result.success) {
          await refreshWatchlist();
          toast.success(t('removedFromWatchlist'));
        } else {
          toast.error(result.message || t('failedToRemoveFromWatchlist'));
        }
      } else {
        const result = await watchlistService.addToCurrentUserWatchlist({
          company_id: ticker,
          notes: '',
          priority: 'normal',
          port_start_point: '',
          port_end_point: '',
        });
        if (result.success) {
          await refreshWatchlist();
          toast.success(t('addedToWatchlist'));
        } else {
          toast.error(result.message || t('failedToAddToWatchlist'));
        }
      }
    } catch (error) {
      toast.error(t('failedToUpdateWatchlist'));
    } finally {
      setWatchlistActionId(null);
    }
  };

  if (tableLoading && companies.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="p-6" style={{ backgroundColor: colors.background.secondary, borderColor: colors.border.light }}>
          <div className="flex flex-col gap-4">
            <div className="h-10 rounded animate-pulse" style={{ backgroundColor: colors.gray[200], width: '100%' }} />
            <div className="flex flex-wrap gap-3 pt-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-9 rounded animate-pulse" style={{ backgroundColor: colors.gray[200], width: 120 }} />
              ))}
            </div>
          </div>
        </Card>
        <TableShimmer rows={10} cardStyle={cardStyle} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and filters card */}
      <Card className="p-6" style={{ backgroundColor: colors.background.secondary, borderColor: colors.border.light }}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                style={{ color: colors.text.tertiary }}
              />
              <Input
                type="text"
                placeholder={t('searchCompaniesPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
                style={{ borderColor: colors.border.light }}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t" style={{ borderColor: colors.border.light }}>
            <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>
              {t('filtersLabel')}
            </span>
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
            >
              <option value="all">{t('allIndustries')}</option>
              {industries.map((ind) => (
                <option key={ind} value={ind}>
                  {ind.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
            <select
              value={filterRegion}
              onChange={(e) => setFilterRegion(e.target.value)}
              className="rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
            >
              <option value="all">{t('allRegions')}</option>
              {REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={filterRiskTier}
              onChange={(e) => setFilterRiskTier(e.target.value)}
              className="rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
            >
              <option value="all">{t('allRiskTiers')}</option>
              {RISK_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {t(`riskLevel${tier === 'high' ? 'High' : tier === 'medium' ? 'Moderate' : 'Low'}`)}
                </option>
              ))}
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="rounded-lg border text-sm px-3 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ borderColor: colors.border.light, color: colors.text.primary, backgroundColor: colors.background.secondary }}
            >
              <option value="all">{t('priority')}</option>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`priority${p === 'high' ? 'High' : p === 'low' ? 'Low' : 'Normal'}`)}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm font-medium hover:underline"
              style={{ color: colors.primary[600] }}
            >
              {t('clearAll')}
            </button>
          </div>
        </div>
      </Card>

      {/* Companies table */}
      {filteredCompanies.length === 0 ? (
        <Card className="p-12 text-center" style={cardStyle}>
          <Building2 className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray[400] }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>
            {t('noCompaniesFound')}
          </h3>
          <p className="mb-4 text-sm" style={{ color: colors.text.secondary }}>
            {t('tryAdjustingFilters')}
          </p>
          <Button variant="outline" size="sm" onClick={clearFilters}>
            {t('clearFilters')}
          </Button>
        </Card>
      ) : (
        <Card style={cardStyle} className="p-6">
          <div className="overflow-x-auto rounded-lg border" style={{ borderColor: colors.border.light }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tableCompany').toUpperCase()}</TableHead>
                <TableHead>{t('tableIndustry').toUpperCase()}</TableHead>
                <TableHead>{t('tableLocation').toUpperCase()}</TableHead>
                <TableHead>{t('tableRiskScore').toUpperCase()}</TableHead>
                <TableHead>{t('tableLastUpdated').toUpperCase()}</TableHead>
                <TableHead>{t('priority').toUpperCase()}</TableHead>
                <TableHead>{t('tableActions').toUpperCase()}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedCompanies.map((company) => {
                const model = company instanceof Company ? company : new Company(company);
                const score = model.overallRiskScore ?? 0;
                const scoreNorm = (score / 10).toFixed(1);
                const tier = getRiskTier(score);
                const riskStyle = getRiskBadgeStyle(tier);
                const industryStyle = getIndustryBadgeStyle(model.industry);
                const { relativeKey, relativeParams, date } = formatRelativeTime(
                  model.riskAnalysisTimestamp || model.updatedAt
                );
                const region = getCompanyRegion(model);
                const locationDisplay = model.country ? `${model.country} / ${region}` : region;
                const sizeType = getCompanySizeType(model.employeeCount);
                const sizeLabel = sizeType
                  ? t(`companyType${sizeType === 'large' ? 'Large' : sizeType === 'medium' ? 'Medium' : 'Small'}Enterprise`)
                  : '—';
                const initials = (model.name || '?').slice(0, 2).toUpperCase();
                const wlItem = watchlistByCompanyId[model.ticker] ?? watchlistByCompanyId[model.id];
                const priority = wlItem?.priority;

                return (
                  <TableRow key={model.ticker}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                          style={{ backgroundColor: colors.primary[100], color: colors.primary[700] }}
                        >
                          {initials}
                        </div>
                        <div>
                          <button
                            type="button"
                            className="font-medium text-left hover:underline block"
                            style={{ color: colors.text.primary }}
                            onClick={() => navigate(`/companies/${model.ticker}`)}
                          >
                            {model.name}
                          </button>
                          <p className="text-xs" style={{ color: colors.text.secondary }}>{sizeLabel}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{ backgroundColor: industryStyle.bg, color: industryStyle.text }}
                      >
                        {model.industry || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: colors.text.secondary }}>
                      {locationDisplay}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium" style={{ color: colors.text.primary }}>{scoreNorm}/10</span>
                      <span
                        className="ml-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{ backgroundColor: riskStyle.bg, color: riskStyle.text }}
                      >
                        {tier === 'high' ? t('riskLevelHigh') : tier === 'medium' ? t('riskLevelModerate') : t('riskLevelLow')}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: colors.text.secondary }}>
                      {relativeKey ? (
                        <>
                          {t(relativeKey, relativeParams)}
                          <span className="block text-xs" style={{ color: colors.text.tertiary }}>{date}</span>
                        </>
                      ) : (
                        date
                      )}
                    </TableCell>
                    <TableCell>
                      {priority ? (
                        <div className="space-y-1">
                          <div className="h-1.5 rounded-full overflow-hidden w-20" style={{ backgroundColor: colors.background.primary }}>
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${getPriorityWidth(priority)}%`,
                                backgroundColor:
                                  getPriorityLevel(priority) === 'riskLevelHigh' ? colors.danger[500]
                                    : getPriorityLevel(priority) === 'riskLevelLow' ? colors.success[500]
                                    : colors.warning[500],
                              }}
                            />
                          </div>
                          <span className="text-xs" style={{ color: colors.text.secondary }}>
                            {t(getPriorityLevel(priority))}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm" style={{ color: colors.text.tertiary }}>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/companies/${model.ticker}`)}
                          className="flex items-center gap-1.5 font-medium text-link hover:underline"
                        >
                          <Eye className="h-4 w-4" />
                          {t('view')}
                        </button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleToggleWatchlist(model.ticker)}
                          disabled={watchlistActionId === model.ticker}
                          aria-label={watchlistIds.has(model.ticker) ? t('removeFromWatchlist') : t('addToWatchlist')}
                        >
                          <Star
                            className="h-4 w-4"
                            fill={watchlistIds.has(model.ticker) ? 'currentColor' : 'none'}
                            style={{ color: watchlistIds.has(model.ticker) ? colors.warning[500] : colors.text.secondary }}
                          />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              {t('showingResults', { from, to, total: totalElements })}
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  />
                </PaginationItem>
                {(() => {
                  const start = Math.max(1, page - 2);
                  const end = Math.min(totalPages, page + 2);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                })().map((p) => (
                  <PaginationItem key={p}>
                    <PaginationLink isActive={p === page} onClick={() => setPage(p)}>
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Companies;
